import { useEffect, useState, RefObject, useRef } from 'react';

type Direction = 'left' | 'right' | 'up' | 'down' | 'none';

interface SwipeState {
    isSwiping: boolean;
    direction: Direction;
    deltaX: number;
    deltaY: number;
}

interface SwiperOptions {
    swipeRef: RefObject<HTMLElement> | null;
    /**
     * 若该元素存在横向溢出（scrollWidth > clientWidth），touchend 时不触发 onSwipeEnd。
     * 用于放大后容器出现横向滚动条时，避免将用户的滚动手势误判为翻页。
     */
    scrollableRef?: RefObject<HTMLElement>;
    onSwipeStart?: (e: TouchEvent) => void;
    onSwipeEnd?: (direction: Direction, e: TouchEvent) => void;
    onSwiping?: (direction: Direction, e: TouchEvent) => void;
}

function useSwipe(options: SwiperOptions): SwipeState;
function useSwipe(swiperRef: RefObject<HTMLElement>): SwipeState;
function useSwipe(options: SwiperOptions | RefObject<HTMLElement>): SwipeState {
    const [isSwiping, setIsSwiping] = useState(false);
    const [direction, setDirection] = useState<Direction>('none');
    const [deltaX, setDeltaX] = useState(0);
    const [deltaY, setDeltaY] = useState(0);

    // 用 ref 存储方向，保证 touchend 闭包里读到的始终是最新值，不依赖 re-render 时机
    const directionRef = useRef<Direction>('none');

    const swiperRef = (options as RefObject<HTMLElement>).current
        ? (options as RefObject<HTMLElement>)
        : (options as SwiperOptions).swipeRef;
    const swipeOptions = (options as SwiperOptions).swipeRef ? (options as SwiperOptions) : ({} as SwiperOptions);

    const clientX = useRef(0);
    const clientY = useRef(0);
    const isSingleFinger = useRef(true);

    useEffect(() => {
        const handleSwipingStart = (e: TouchEvent) => {
            if (e.touches.length > 1) {
                isSingleFinger.current = false;
                setIsSwiping(false);
                return;
            }
            isSingleFinger.current = true;
            directionRef.current = 'none';
            setIsSwiping(true);
            clientX.current = e.touches[0].clientX;
            clientY.current = e.touches[0].clientY;
            swipeOptions.onSwipeStart?.(e);
        };

        const handleSwipingEnd = (e: TouchEvent) => {
            if (!isSingleFinger.current) {
                return;
            }
            // 若绑定了 scrollableRef 且容器存在横向溢出，说明用户在滚动放大后的内容，不触发翻页
            const scrollable = swipeOptions.scrollableRef?.current;
            if (scrollable && scrollable.scrollWidth > scrollable.clientWidth) {
                setIsSwiping(false);
                directionRef.current = 'none';
                setDirection('none');
                return;
            }
            swipeOptions.onSwipeEnd?.(directionRef.current, e);
            setIsSwiping(false);
            directionRef.current = 'none';
            setDirection('none');
        };

        const handleSwiping = (e: TouchEvent) => {
            if (!isSingleFinger.current) {
                return;
            }
            const dx = e.touches[0].clientX - clientX.current;
            const dy = e.touches[0].clientY - clientY.current;

            let newDirection: Direction;
            if (Math.abs(dx) > Math.abs(dy)) {
                newDirection = dx > 0 ? 'right' : 'left';
            } else {
                newDirection = dy > 0 ? 'down' : 'up';
            }

            directionRef.current = newDirection;
            setDirection(newDirection);
            setDeltaX(dx);
            setDeltaY(dy);
            swipeOptions.onSwiping?.(directionRef.current, e);
        };

        // 在 effect 运行时捕获 DOM 节点，而非在 cleanup 时读取 swiperRef.current。
        // 原因：React 在 commit 阶段先清空 ref（element unmount → ref.current = null），
        // 再执行 effect cleanup。若通过 ref 读取，cleanup 时 ref.current 已为 null，
        // 导致无法移除监听器 —— 而 touch 事件存在"捕获特性"：touchstart 绑定的目标
        // 会持续收到同一触摸序列的 touchmove/touchend，即使元素已脱离 DOM。
        // 使用本地变量 el 可确保 cleanup 能正确移除脱离 DOM 的元素上的监听器。
        const el = swiperRef?.current;
        if (el) {
            el.addEventListener('touchstart', handleSwipingStart, { passive: true });
            el.addEventListener('touchmove', handleSwiping, { passive: true });
            el.addEventListener('touchend', handleSwipingEnd, { passive: true });
        }

        return () => {
            if (el) {
                el.removeEventListener('touchstart', handleSwipingStart);
                el.removeEventListener('touchmove', handleSwiping);
                el.removeEventListener('touchend', handleSwipingEnd);
            }
        };
    }, [swiperRef, swipeOptions]);

    return {
        isSwiping,
        direction,
        deltaX,
        deltaY,
    };
}

export default useSwipe;
